import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { Checkbox, CheckboxChangeEvent, Input } from '@progress/kendo-react-inputs';
import * as React from 'react';
import { fetchApi } from '../../services/api';
import { IDName } from '../../types/idname';
import { JsonResponse } from 'TypeGen/json-response';
import { FieldWrapper } from '@progress/kendo-react-form';

interface Props {}

type CreateTurnDownOptions = {
  DeclineReasons: IDName[];
};

export type TurnDownPayload = {
  QuoteID: number;
  OrderID: number | null;
  VehicleID: number;
  DriverID: number;
  Reset: boolean;
  Legs: number;
  LoadedDistance: number;
  EmptyDistance: number;
  Linehaul: number;
  Accessorial: number;
  Fuel: number;
  Comment: string;
};

export type CreateTurnDownDialogHandle = {
  open: (payload: TurnDownPayload) => void;
};

const CreateTurnDownDialog = React.forwardRef<CreateTurnDownDialogHandle, Props>((_props, ref) => {
  const [visible, setVisible] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [reasons, setReasons] = React.useState<IDName[]>([]);
  const [reasonCode, setReasonCode] = React.useState<number | null>(null);
  const [payload, setPayload] = React.useState<TurnDownPayload | null>(null);
  const [reset, setReset] = React.useState(false);
  const [comment, setComment] = React.useState('');

  const resetState = React.useCallback(() => {
    setVisible(false);
    setLoading(false);
    setSubmitting(false);
    setReasons([]);
    setReasonCode(null);
    setPayload(null);
    setReset(false);
    setComment('');
  }, []);

  const loadReasons = React.useCallback(() => {
    setLoading(true);
    fetchApi('/api/Quote/GetCreateDriverTurnDown')
      .then((data: CreateTurnDownOptions) => {
        setReasons(data.DeclineReasons);
      })
      .catch((e) => {
        if (!e?.status) alert('Unable to load turn down options!');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const open = React.useCallback((incomingPayload: TurnDownPayload) => {
    setVisible(true);
    setSubmitting(false);
    setReasons([]);
    setReasonCode(null);
    setPayload(incomingPayload);
    setReset(!!incomingPayload.Reset);
    setComment(incomingPayload.Comment ?? '');
    loadReasons();
  }, [loadReasons]);

  React.useImperativeHandle(ref, () => ({ open }), [open]);

  const handleReasonChange = React.useCallback((e: DropDownListChangeEvent) => {
    const reason = e.value as IDName | null;
    setReasonCode(reason && reason.ID !== 0 ? reason.ID : null);
  }, []);

  const handleResetChange = React.useCallback((e: CheckboxChangeEvent) => {
    setReset(e.value);
  }, []);

  const close = React.useCallback(() => {
    if (submitting) {
      return;
    }
    resetState();
  }, [resetState, submitting]);

  const submit = React.useCallback(() => {
    if (!payload) {
      return;
    }

    if (reasons.length > 0 && !reasonCode) {
      alert('Please select a turn down reason.');
      return;
    }

    const data = {
      ...payload,
      ReasonCode: reasonCode,
      Reset: reset,
      Comment: comment,
    };

    setSubmitting(true);

    fetchApi('/api/Quote/CreateDriverTurnDown', data, 'POST')
      .then((response: JsonResponse) => {
        if (response.Success) {
          alert('Successfully created turndown!');
          resetState();
        } else {
          alert(response.ErrorMessage);
          setSubmitting(false);
        }
      })
      .catch(() => {
        alert('Unable to create turndown!');
        setSubmitting(false);
      });
  }, [comment, payload, reasonCode, reasons.length, reset, resetState]);

  const selectedReason = React.useMemo(() => {
    return reasons.find(x => x.ID === reasonCode) ?? undefined;
  }, [reasonCode, reasons]);

  if (!visible || !payload) {
    return null;
  }

  return <Dialog title="Create Turn Down" onClose={close} className="dialog-w525">
    <form className="k-form k-form-md">
      <FieldWrapper>
        <DropDownList
          style={{ width: '100%' }}
          data={reasons}
          dataItemKey="ID"
          textField="Name"
          label='Decline Reason'
          value={selectedReason}
          defaultItem={{ ID: 0, Name: 'Select reason...' }}
          onChange={handleReasonChange}
        />
      </FieldWrapper>
      <FieldWrapper>
        <Input
          style={{ width: '100%' }}
          maxLength={120}
          value={comment}
          label='Comment'
          onChange={(e) => setComment(e.value)}
        />
      </FieldWrapper>
      <FieldWrapper>
        <Checkbox
          label="Reset Last Available"
          checked={reset}
          onChange={handleResetChange}
        />
      </FieldWrapper>
    </form>
    <DialogActionsBar>
      <Button disabled={submitting} onClick={close}>Cancel</Button>
      <Button
        themeColor="primary"
        disabled={loading || submitting}
        onClick={submit}
      >
        {submitting ? 'Saving...' : 'Create Turn Down'}
      </Button>
    </DialogActionsBar>
  </Dialog>;
});

export default CreateTurnDownDialog;
